import { useEffect, useState } from "react";
import React from "react";
import { useRecoilState } from "recoil";
import { App, Layout } from "antd";
import {
  Authenticator,
  ThemeProvider,
  useTheme,
  defaultDarkModeOverride,
  useAuthenticator,
  View,
} from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { UserOutlined } from "@ant-design/icons";
import "./App.css";
import NavBar from "./Components/NavBar";
import SideBar from "./Components/SideBar";
import PageContent from "./Components/PageContent";
import MobileMenu from "./Components/MobileMenu";
import { allSitesAtom } from "./Atoms/allSitesAtom";
import {
  getRecipientShares,
  getTools,
  getUser,
  updateRecipient,
  getMessages,
  getProjects,
  getReports,
  getReportLogs,
  getSharedStores,
  getSharedStore,
} from "./API";
import { Row, Text, Icon, Graph, Alert, Button } from "./Components";
import AppRoutes from "./Components/AppRoutes";
import { withCookies } from "react-cookie";
import _ from "lodash";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ReactComponent as Logo } from "./Assets/SVG/Logo.svg";
import { useLocation } from "react-router-dom";
import { Shell } from "./Components/AppShell/Shell";
import { Box } from "@mantine/core";
//TODO: SWITCH TO AXIOS FROM FETCH

function getItem(label, key, icon, children, type) {
  return {
    key,
    icon,
    children,
    label,
    type,
  };
}

function initializeSitesStructure(projectData, recipientShares, dbUser) {
  return {
    graphData: new Map(),
    graphData10: new Map(),
    graphData100: new Map(),
    graphData1000: new Map(),
    graphDataHour: new Map(),
    graphDataDay: new Map(),
    graphDataMonth: new Map(),
    liveScope: new Map(),
    dayScope: new Map(),
    weekScope: new Map(),
    monthScope: new Map(),
    maxScope: new Map(),
    customScope: new Map(),
    stores: projectData,
    recipientShares,
    user: dbUser,
  };
}

const EntryPage = () => {
  const { tokens } = useTheme();
  const { user } = useAuthenticator((context) => [context.user]);
  const [time, setTime] = useState(new Date());
  const [allSites, setAllSites] = useRecoilState(allSitesAtom);
  const [loading, setLoading] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [items, setItems] = useState([]);
  const [recipientShares, setRecipientShares] = useState(
    allSites["recipientShares"]
  );
  const [currentShareID, setCurrentShareID] = useState(undefined);
  const [acceptShare, setAcceptShare] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [acceptInitiated, setAcceptInitiated] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectInitiated, setRejectInitiated] = useState(false);
  console.log("Items:", items);
  console.log("All Sites:", allSites);
  const [rootSubmenuKeys, setRootSubmenuKeys] = useState([]);
  const messageAttributes =
    "msgType,message_id,settings,alert_id,ts,message,alertContent,mac,toolType";

  const components = {
    Header() {
      return (
        <View
          textAlign="center"
          style={{
            borderWidth: 1,
            borderColor: "black", // Change the color if needed
            backgroundColor: "white",
            padding: 20, // Adjust padding as needed
          }}
          padding={tokens.space.large}
        >
          <Logo style={{ height: 55 }} className="logo" />
        </View>
      );
    },
  };

  useEffect(() => {
    if (_.isEqual(allSites, {})) setItems([]);
    if (!_.isEqual(allSites.fullscreen, fullscreen))
      setFullscreen(allSites.fullscreen);
    if (allSites.repull) {
      let tempAllSites = _.cloneDeep(allSites);
      delete tempAllSites["repull"];
      console.log("allSites MobileEntry Updating AllSites to ", tempAllSites);
      setAllSites(tempAllSites);
      setTime(new Date());
    }
  }, [allSites]);

  useEffect(() => {
    if (acceptLoading) {
      try {
        updateRecipient(
          user.signInUserSession.idToken.jwtToken,
          currentShareID,
          { shareNotify: true, acceptState: true }
        ).then(() => {
          setAcceptLoading(false);
          setRejectLoading(false);
          setTime(new Date());
        });
      } catch (error) {
        console.log("DELETE ERROR:", error);
      }
    } else {
      if (acceptInitiated) {
        setAcceptShare(false);
        setAcceptInitiated(false);
        setCurrentShareID(undefined);
      }
    }
  }, [acceptLoading, acceptInitiated]);

  useEffect(() => {
    if (rejectLoading) {
      try {
        updateRecipient(
          user.signInUserSession.idToken.jwtToken,
          currentShareID,
          { shareNotify: true, acceptState: false }
        ).then(() => {
          setAcceptLoading(false);
          setRejectLoading(false);
          setTime(new Date());
        });
      } catch (error) {
        console.log("DELETE ERROR:", error);
      }
    } else {
      if (rejectInitiated) {
        setAcceptShare(false);
        setRejectInitiated(false);
        setCurrentShareID(undefined);
      }
    }
  }, [rejectLoading, rejectInitiated]);

  // Fetch initial data for global state - User, Stores, Tools, Recipients
  const fetchData = async () => {
    setLoading(true);
    if (
      user &&
      _.has(user, "signInUserSession") &&
      _.has(user.signInUserSession, "idToken") &&
      _.has(user.signInUserSession.idToken, "jwtToken")
    ) {
      console.log("USER SIGNED IN and has token:", user);

      try {
        const token = user.signInUserSession.idToken.jwtToken;

        // Get Current User
        let dbUser = await getUser({ token });

        // Fetch Stores for user with related info
        let projectData;
        try {
          projectData = await getProjects();
        } catch (error) {
          console.error("Error fetching project data:", error);
        }

        // Get Stores shared with user
        let sharedStores;
        try {
          sharedStores = await getSharedStores({ token });
          if (sharedStores) {
            // Add 'sharedStore: true' field to label as shared store
            const sharedStoresWithLabel = sharedStores.map((store) => ({
              ...store,
              sharedStore: true,
            }));

            // Doing this to avoid having to change data references across the app
            // Could be improved over time
            if (projectData && projectData.Items) {
              // Combine sharedStoresWithLabel with projectData.Items
              projectData.Items = projectData.Items.concat(
                sharedStoresWithLabel
              );
              console.log("Added Shared Stores");
              console.log("projectData", projectData);
            } else {
              // Assign sharedStoresWithLabel to projectData.Items if projectData is undefined
              projectData = { Items: sharedStoresWithLabel };
              console.log("Assign sharedStoresWithLabel", projectData);
            }
          }
        } catch (error) {
          console.error("Error fetching shared stores:", error);
        }

        // Get Tool Data for Stores
        let toolsData;
        try {
          toolsData = await getTools({ token });
        } catch (error) {
          console.error("Error fetching tools data:", error);
        }

        // Initialize sites structure with everything needed for global state
        let recipientShares;
        if (toolsData) {
          try {
            recipientShares = await getRecipientShares({ token });
          } catch (error) {
            console.error("Error fetching recipient shares:", error);
          }
        }

        let sites = {
          ...initializeSitesStructure(
            projectData,
            recipientShares || [],
            dbUser || {}
          ),
          ...(toolsData ? toolsData.items : {}),
        };

        setAllSites(sites);
      } catch (error) {
        console.error("Error in data fetching:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Run This everytime the user/time change
  useEffect(() => {
    fetchData();
  }, [user, time]);

  return (
    <>
      {/* Refactored the way were displaying the login page or the rest of the app. This way changes made to the login component dont effect the entire app */}
      {user &&
      _.has(user, "signInUserSession") &&
      _.has(user.signInUserSession, "idToken") &&
      _.has(user.signInUserSession.idToken, "jwtToken") ? (
        <Shell>
          <AppRoutes />
        </Shell>
      ) : (
        <div className="authenticator">
          <Box
            mt={{ base: 100, md: 100, lg: 200 }}
            mr={{ base: 0, md: 0, lg: 475 }}
          >
            <Authenticator
              components={components}
              formFields={{
                signIn: {
                  username: {
                    placeholder: "Enter Your Email",
                    isRequired: true,
                    label: "Email",
                  },
                },
                signUp: {
                  username: {
                    placeholder: "Enter Your Email",
                    isRequired: true,
                    label: "Email",
                  },
                },
                resetPassword: {
                  username: { placeholder: "Enter Your Email", label: "Email" },
                },
              }}
            />
          </Box>
        </div>
      )}
    </>
  );
};

export default withCookies(EntryPage);
